import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Coins, Heart, Send, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

const PRESET_AMOUNTS = [10, 25, 50, 100, 250, 500, 1000];

const TipModal = ({ isOpen, onClose, creator, userCredits, onSuccess }) => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const currentAmount = customAmount ? parseInt(customAmount, 10) : selectedAmount;
  const isValidAmount = currentAmount && currentAmount > 0 && currentAmount <= userCredits;

  const handlePresetClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
    setError("");
  };

  const handleCustomChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setCustomAmount(value);
    setSelectedAmount(null);
    setError("");
  };

  const handleSendTip = async () => {
    if (!currentAmount || currentAmount < 1) {
      setError("Please select or enter an amount");
      return;
    }

    if (currentAmount > userCredits) {
      setError("Insufficient credits");
      return;
    }

    setSending(true);
    setError("");

    try {
      const response = await axios.post(`${API}/public/tips/send`, {
        creator_id: creator.id,
        amount: currentAmount,
        message: message.trim() || null
      });

      setSuccess(true);
      
      // Notify parent of success after a short delay
      setTimeout(() => {
        onSuccess(response.data);
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send tip");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedAmount(null);
    setCustomAmount("");
    setMessage("");
    setError("");
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-gray-900 rounded-2xl max-w-md w-full overflow-hidden border border-gray-700/50 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-500/20 rounded-full">
                <Heart className="w-5 h-5 text-pink-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Send a Tip</h3>
                <p className="text-sm text-gray-400">to {creator?.name || creator?.username}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-800 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-8 text-center"
            >
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mb-2">Tip Sent!</h4>
              <p className="text-gray-400">
                You sent <span className="text-amber-400 font-semibold">{currentAmount} credits</span> to {creator?.name || creator?.username}
              </p>
            </motion.div>
          ) : (
            <div className="p-6 space-y-6">
              {/* User Balance */}
              <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <span className="text-gray-400 text-sm">Your Balance</span>
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 font-semibold">{userCredits} credits</span>
                </div>
              </div>

              {/* Preset Amounts */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Select Amount
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {PRESET_AMOUNTS.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handlePresetClick(amount)}
                      disabled={amount > userCredits}
                      className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                        selectedAmount === amount
                          ? "bg-pink-500 text-white ring-2 ring-pink-400 ring-offset-2 ring-offset-gray-900"
                          : amount > userCredits
                            ? "bg-gray-800/50 text-gray-600 cursor-not-allowed"
                            : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      {amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Or enter custom amount
                </label>
                <div className="relative">
                  <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={customAmount}
                    onChange={handleCustomChange}
                    placeholder="Enter amount"
                    className={`w-full bg-gray-800 border rounded-lg py-3 pl-10 pr-20 text-white placeholder-gray-500 focus:outline-none transition-colors ${
                      customAmount && parseInt(customAmount, 10) > userCredits
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-700 focus:border-pink-500"
                    }`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                    credits
                  </span>
                </div>
                {customAmount && parseInt(customAmount, 10) > userCredits && (
                  <p className="text-red-400 text-xs mt-1">Exceeds your balance</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Add a message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Say something nice..."
                  maxLength={500}
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-pink-500 transition-colors resize-none"
                />
                <p className="text-gray-500 text-xs mt-1 text-right">{message.length}/500</p>
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
                >
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </motion.div>
              )}
            </div>
          )}

          {/* Footer */}
          {!success && (
            <div className="px-6 py-4 border-t border-gray-700/50 flex items-center justify-between">
              <div>
                {currentAmount > 0 && (
                  <p className="text-sm text-gray-400">
                    Sending <span className="text-pink-400 font-semibold">{currentAmount} credits</span>
                  </p>
                )}
              </div>
              <button
                onClick={handleSendTip}
                disabled={!isValidAmount || sending}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-tip-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Send Tip
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TipModal;
